import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function TelecommandeClavierPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Aucun token de session fourni.');
      return;
    }

    let cancelled = false;

    const connect = async () => {
      try {
        const { error: updateErr } = await supabase
          .from('remote_keyboard_sessions')
          .update({ is_connected: true, connected_at: new Date().toISOString() })
          .eq('session_token', token);

        if (updateErr) throw updateErr;

        const ch = supabase.channel(`rk-${token}`, {
          config: { broadcast: { self: false } },
        });

        channelRef.current = ch;

        ch.subscribe((status) => {
          if (status === 'SUBSCRIBED' && !cancelled) {
            setConnected(true);
          }
          if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !cancelled) {
            setError('Erreur de connexion au canal. Rechargez la page.');
          }
        });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[Telecommande] Connection error:', msg);
        if (!cancelled) {
          setError('Impossible de se connecter. Vérifiez le QR code.');
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [token]);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text || !channelRef.current) return;

    setSending(true);

    channelRef.current.send({
      type: 'broadcast',
      event: 'text',
      payload: { text },
    });

    setValue('');
    setSending(false);
    setSent(true);
    setTimeout(() => setSent(false), 2000);
  }, [value]);

  const handleClear = useCallback(() => {
    setValue('');
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background-50 px-4">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <div className="mb-3 flex justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <i className="ri-error-warning-line text-2xl text-red-500"></i>
            </span>
          </div>
          <h1 className="mb-2 text-lg font-semibold text-foreground-900">Erreur de connexion</h1>
          <p className="text-sm text-foreground-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background-50 px-4">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
            </span>
          </div>
          <h1 className="mb-2 text-lg font-semibold text-foreground-900">Connexion en cours...</h1>
          <p className="text-sm text-foreground-500">
            Veuillez patienter, connexion au clavier distant.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <header className="border-b border-foreground/10 bg-background-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100">
            <i className="ri-check-line text-green-600"></i>
          </span>
          <div>
            <h1 className="text-base font-semibold text-foreground-900">Clavier connecté</h1>
            <p className="text-xs text-foreground-500">
              Tapez votre texte, puis appuyez sur Envoyer
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 px-4 py-4">
        <textarea
          className="h-full min-h-[55vh] w-full resize-none rounded-xl border border-foreground/10 bg-white p-4 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          placeholder="Tapez votre texte ici..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      </div>

      <footer className="border-t border-foreground/10 bg-background-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <p className="text-xs text-foreground-500">Clavier distant</p>
            {sent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                <i className="ri-check-line"></i>
                Envoyé !
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClear}
              disabled={!value}
              className="rounded-lg bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-200 disabled:opacity-40 whitespace-nowrap cursor-pointer"
            >
              Effacer
            </button>
            <button
              onClick={handleSend}
              disabled={!value.trim() || sending}
              className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-40 whitespace-nowrap cursor-pointer"
            >
              <i className={`${sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}`}></i>
              Envoyer
            </button>
          </div>
        </div>
        <p className="mt-1.5 text-[10px] text-foreground-400">
          Astuce : Ctrl+Entrée pour envoyer rapidement
        </p>
      </footer>
    </div>
  );
}